import React, { useState } from 'react';
import { getIn, useFormikContext } from 'formik';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { TEMPLATE_BUILDER_FORM_KEYS } from '~/components/GenericTemplates/utils/genericTemplatesUtils';

import { MOI_METHODS_FIELD_ID } from './MoiEmailMethodsMultiSelectFormik';
import type { MoiEmailTemplateFormValues } from './types';

interface MoiEmailTargetGuardProps {
  // Inert when true (the Default template's target is locked; nothing to guard).
  disabled?: boolean;
  children: React.ReactNode;
}

// Keys the MUI selects/checkboxes react to — intercepted so keyboard interaction can't slip past
// the pointer guard.
const INTERACTION_KEYS = new Set(['Enter', ' ', 'ArrowDown', 'ArrowUp']);

/**
 * Guards the template-target section (sub-orgs / LOBs / coverages / countries / states): while any
 * scope-dependent value exists (selected MOI methods, title/body draft, attached document template),
 * the first interaction with a target input is intercepted and a confirmation is shown BEFORE the
 * input reacts — mirroring the email template builder's "Confirm Selection Change" dialog. Confirm
 * erases those values (they belong to the previous target) and unlocks the section; Cancel leaves
 * everything exactly as it was — the target change never happens, so nothing needs reverting.
 *
 * The wrapper uses `display: contents` so it doesn't disturb the parent grid layout; React's
 * synthetic capture events still fire through it. The dialog is rendered outside the intercepted
 * subtree so its own buttons aren't captured.
 */
const MoiEmailTargetGuard: React.FC<MoiEmailTargetGuardProps> = ({ disabled = false, children }) => {
  const { values, setFieldValue } = useFormikContext<MoiEmailTemplateFormValues>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const documentTemplateId = getIn(values, 'document_template_id');
  const hasValuesToErase =
    ((getIn(values, MOI_METHODS_FIELD_ID) as string[] | undefined)?.length ?? 0) > 0 ||
    !!getIn(values, TEMPLATE_BUILDER_FORM_KEYS.TITLE_TEMPLATE) ||
    !!getIn(values, TEMPLATE_BUILDER_FORM_KEYS.BODY_TEMPLATE) ||
    (documentTemplateId !== null && documentTemplateId !== undefined);

  const isArmed = hasValuesToErase && !disabled;

  const intercept = (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setIsConfirmOpen(true);
  };

  const handleConfirm = () => {
    setFieldValue(MOI_METHODS_FIELD_ID, []);
    setFieldValue(TEMPLATE_BUILDER_FORM_KEYS.TITLE_TEMPLATE, '');
    setFieldValue(TEMPLATE_BUILDER_FORM_KEYS.BODY_TEMPLATE, '');
    setFieldValue('document_template_id', null);
    setIsConfirmOpen(false);
  };

  return (
    <>
      <div
        className="contents"
        onMouseDownCapture={isArmed ? intercept : undefined}
        onClickCapture={isArmed ? intercept : undefined}
        onKeyDownCapture={
          isArmed
            ? (event) => {
                if (INTERACTION_KEYS.has(event.key)) {
                  intercept(event);
                }
              }
            : undefined
        }
      >
        {children}
      </div>
      {isConfirmOpen ? (
        <CardDialog
          isDialog
          title="Confirm Selection Change"
          onClose={() => setIsConfirmOpen(false)}
          fullWidth
          maxWidth="sm"
        >
          <p>
            Are you sure you want to change your selection? Changing it will erase the selected MOI methods, the
            existing template draft and the attached document template
          </p>
          <div className="mt-16 flex w-full justify-end">
            <CancelButton onClick={() => setIsConfirmOpen(false)} />
            <Button variant="contained" color="primary" onClick={handleConfirm}>
              Confirm
            </Button>
          </div>
        </CardDialog>
      ) : null}
    </>
  );
};

export default MoiEmailTargetGuard;
